/**
 * Search, filters and totals for the map.
 *
 * Extracted from MapPage so the desktop sidebar and the mobile sheet render the
 * same controls. They were previously only in the sidebar, which was
 * `hidden lg:flex` - so on a phone there was no search, no level or branch
 * filter, no depth and no cross-level toggle, and the page apologised with
 * "rotate to landscape, or open on a wider screen".
 */

import { LEVELS, levelColor, type Branch, type Graph, type KanjiNode, type Level } from '../api'
import { Spinner } from './ui'

export interface MapStats {
  byLevel: Map<string, number>
  radical: number
  radicalAndKanji: number
  componentOnly: number
  mastered: number
}

interface MapControlsProps {
  term: string
  onTerm: (v: string) => void
  searching: boolean
  results: KanjiNode[]
  onPick: (char: string) => void

  level: Level
  onLevel: (v: string) => void
  branch: string | null
  branches: Branch[]
  onBranch: (v: string | null) => void
  depth: number
  onDepth: (v: number) => void
  includeStrokes: boolean
  onIncludeStrokes: (v: boolean) => void
  showOffLevel: boolean
  onShowOffLevel: (v: boolean) => void

  graph: Graph | null
  stats: MapStats | null
  signedIn: boolean
}

export default function MapControls(props: MapControlsProps) {
  const {
    term, onTerm, searching, results, onPick,
    level, onLevel, branch, branches, onBranch,
    depth, onDepth, includeStrokes, onIncludeStrokes,
    showOffLevel, onShowOffLevel, graph, stats, signedIn,
  } = props

  return (
    <>
      <section className="card p-3">
        <h2 className="label">Search</h2>
        <div className="relative">
          <input
            className="input pr-11"
            placeholder="kanji, meaning or romaji…"
            value={term}
            onChange={(e) => onTerm(e.target.value)}
            aria-label="Search kanji"
            // Stops phone keyboards from capitalising and autocorrecting romaji.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
          />
          {term && (
            <button
              type="button"
              onClick={() => onTerm('')}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center
                         rounded-lg text-ink-500 active:bg-ink-800 hover:text-slate-300"
            >
              ✕
            </button>
          )}
        </div>
        {searching && (
          <div className="mt-2">
            <Spinner label="Searching…" />
          </div>
        )}
        {results.length > 0 && (
          <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto overscroll-contain">
            {results.map((n) => (
              <li key={n.char}>
                <button
                  type="button"
                  onClick={() => onPick(n.char)}
                  className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2 py-1.5
                             text-left active:bg-ink-800 hover:bg-ink-800"
                >
                  <span className="jp text-xl leading-none">{n.char}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs text-slate-200">{n.meaning}</span>
                    <span className="block truncate text-[10px] text-ink-500">{n.romaji}</span>
                  </span>
                  {n.level && (
                    <span className="text-[9px] font-bold" style={{ color: levelColor[n.level] }}>
                      {n.level}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card p-3">
        <h2 className="label">Filter</h2>
        <label className="label !mb-1 !normal-case !tracking-normal" htmlFor="f-level">
          Level
        </label>
        <select
          id="f-level"
          className="input"
          value={level}
          onChange={(e) => onLevel(e.target.value)}
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>

        <label className="label !mb-1 !mt-3 !normal-case !tracking-normal" htmlFor="f-branch">
          Branch
        </label>
        <select
          id="f-branch"
          className="input"
          value={branch ?? ''}
          onChange={(e) => onBranch(e.target.value || null)}
        >
          <option value="">All branches</option>
          {branches.map((b) => (
            <option key={b.char} value={b.char}>
              {b.char} — {b.meaning} ({b.family})
            </option>
          ))}
        </select>

        <label className="label !mb-1 !mt-3 !normal-case !tracking-normal" htmlFor="f-depth">
          Component depth: {depth}
        </label>
        <input
          id="f-depth"
          type="range"
          min={1}
          max={4}
          value={depth}
          onChange={(e) => onDepth(Number(e.target.value))}
          // h-11 gives the thumb a 44px tall track to be dragged within.
          className="h-11 w-full accent-brand-500"
        />

        {/* min-h on the whole label makes the text a tap target too, not just the
            12px checkbox. */}
        <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={includeStrokes}
            onChange={(e) => onIncludeStrokes(e.target.checked)}
            className="h-5 w-5 shrink-0 accent-brand-500"
          />
          Show single strokes
        </label>

        <label className="flex min-h-[44px] cursor-pointer items-start gap-3 py-1 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={showOffLevel}
            onChange={(e) => onShowOffLevel(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 accent-brand-500"
          />
          <span>
            Show kanji from other levels
            <span className="mt-0.5 block text-[10px] leading-snug text-ink-600">
              When you open a character, include the kanji outside {level} that use it,
              drawn dimmed. Turn off to stay strictly within {level}.
            </span>
          </span>
        </label>
      </section>

      {stats && graph && (
        <section className="card p-3">
          <h2 className="label">Totals</h2>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <Tile label="Nodes" value={graph.nodes.length} />
            <Tile label="Links" value={graph.edges.length} />
          </div>
          <h3 className="label">By level</h3>
          <ul className="space-y-1">
            {LEVELS.map((l) => (
              <li key={l} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5">
                  <span
                    className="inline-flex h-4 w-7 items-center justify-center rounded-full text-[9px] font-bold"
                    style={{ background: `${levelColor[l]}22`, color: levelColor[l] }}
                  >
                    {l}
                  </span>
                </span>
                <span className="text-slate-300">{stats.byLevel.get(l) ?? 0}</span>
              </li>
            ))}
          </ul>
          <h3 className="label mt-3">By role</h3>
          <ul className="space-y-1 text-xs">
            <Row badge="RK" hint="radical and kanji" value={stats.radicalAndKanji} />
            <Row badge="R" hint="radical only" value={stats.radical} />
            <Row badge="C" hint="component" value={stats.componentOnly} />
          </ul>
          {signedIn && (
            <p className="mt-3 rounded-lg bg-brand-500/10 px-2 py-1.5 text-[11px] text-brand-400">
              {stats.mastered} mastered on this map
            </p>
          )}
        </section>
      )}

      <section className="card p-3 text-[11px] leading-relaxed text-ink-500">
        <h2 className="label">How to use</h2>
        <ol className="list-decimal space-y-1 pl-4">
          <li>Drag with one finger to move the map, pinch with two to zoom.</li>
          <li>Tap the red arrow to open or close a branch.</li>
          <li>Tap a node to see its breakdown, readings and examples.</li>
          <li>Search a character to jump straight to it.</li>
        </ol>
      </section>
    </>
  )
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-ink-700/70 bg-ink-850/70 px-2 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-wide text-ink-500">{label}</div>
      <div className="text-sm font-bold text-slate-100">{value}</div>
    </div>
  )
}

function Row({ badge, hint, value }: { badge: string; hint: string; value: number }) {
  return (
    <li className="flex items-center justify-between">
      <span className="flex items-center gap-1.5">
        <span className="rounded-full bg-ink-700/80 px-1.5 py-px text-[9px] font-bold text-ink-400">
          {badge}
        </span>
        <span className="text-ink-500">{hint}</span>
      </span>
      <span className="text-slate-300">{value}</span>
    </li>
  )
}
